import { captureException } from '@sentry/nextjs';

import { routes } from '@/utils/routes';

import {
  MATCHA_JWT_HEADER,
  MATCHA_POW_FALLBACK_HEADER,
  MATCHA_POW_SOLVE_MS_HEADER,
  MATCHA_POW_SOLVE_PATH_HEADER,
} from './constants';
import type { PowFallbackReason } from './constants';
import type { MatchaJwt } from './types';

let cached: MatchaJwt | null = null;
let inflight: Promise<string> | null = null;

/**
 * Session memory of whether PoW is active server-side. Only `off` sticks (a 404
 * stops us probing). While `active`, each refresh fetches a fresh single-use
 * challenge, but the minted token is cached, so a user solves at most once per ~60s.
 */
type PowState = 'unknown' | 'active' | 'off';
let powState: PowState = 'unknown';

// Low on purpose: PoW runs ~once per ~60s (token is cached), so the solver should
// not grab half a user's cores.
const SOLVE_CONCURRENCY = 4;

// performance.now() is not guaranteed present in every runtime this bundle runs in.
const nowMs = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Validates a mint response and caches it. */
const acceptToken = (data: MatchaJwt): string => {
  if (!data?.token || typeof data.exp !== 'number') {
    throw new Error('Token endpoint returned an invalid response');
  }
  cached = data;
  return data.token;
};

/** Tags which stage of the PoW mint failed, so the caller can fall back tagged. */
class PowError extends Error {
  constructor(readonly reason: PowFallbackReason) {
    super(`proof-of-work mint failed: ${reason}`);
  }
}

/**
 * Solves an altcha challenge, off the main thread when Workers exist, else inline.
 * `altcha-lib`, the fallback PBKDF2 algorithm, and the worker source are imported
 * dynamically so the solver never enters the bundle on the `off` path (after the
 * first 404).
 *
 * The worker path silently degrades to the main thread on any setup/messaging
 * failure (CSP, asset origin, missing chunk), but the underlying error is reported
 * to Sentry first: production telemetry shows the worker path never succeeds, and
 * that swallowed error is the only signal that says why. A genuine main-thread solve
 * failure throws `solve_main`; a worker failure that the main thread also can't
 * recover from surfaces as `solve_main` (we got far enough to run it).
 *
 * @throws {PowError} `solve_main` if no solution is produced.
 */
const solvePowChallenge = async (
  challenge: unknown,
): Promise<{ solution: unknown; path: 'worker' | 'main' }> => {
  const { solveChallenge, solveChallengeWorkers } = await import('altcha-lib');

  if (typeof Worker !== 'undefined') {
    try {
      // Lazy + only here, so the worker stays out of the off-path bundle.
      const { createPowWorker } = await import('./pow-worker');
      const solution = await solveChallengeWorkers({
        challenge: challenge as Parameters<typeof solveChallengeWorkers>[0]['challenge'],
        concurrency: SOLVE_CONCURRENCY,
        createWorker: () => createPowWorker(),
      });
      if (solution) return { solution, path: 'worker' };
      // No throw, but no solution either: still a worker-path failure worth seeing.
      captureException(new Error('PoW worker solver returned no solution'), {
        tags: { powStage: 'solve_worker' },
      });
    } catch (err) {
      // The swallowed worker error (SecurityError, foreign-origin importScripts
      // refusal, CSP) is the diagnostic we lack; report it, then try main-thread.
      captureException(err, { tags: { powStage: 'solve_worker' } });
    }
  }

  const { deriveKey } = await import('altcha-lib/algorithms/pbkdf2');
  const solution = await solveChallenge({
    challenge: challenge as Parameters<typeof solveChallenge>[0]['challenge'],
    deriveKey,
  });
  if (!solution) throw new PowError('solve_main');
  return { solution, path: 'main' };
};

/**
 * Mints via the legacy `GET /api/jwt`. A set `fallbackReason` tags the request so the
 * server records a failed PoW path (observe counts it; enforce still 403s).
 *
 * @throws On a non-2xx (e.g. 403 under enforce) or invalid body.
 */
const mintLegacy = async (fallbackReason?: PowFallbackReason): Promise<string> => {
  const res = await fetch(routes.api.QUOTE_JWT, {
    method: 'GET',
    cache: 'no-store',
    ...(fallbackReason ? { headers: { [MATCHA_POW_FALLBACK_HEADER]: fallbackReason } } : {}),
  });
  if (!res.ok) throw new Error(`Failed to mint quote jwt (${res.status})`);
  return acceptToken((await res.json()) as MatchaJwt);
};

/** Runs the handshake: a 404 from the challenge endpoint means PoW is off (legacy mint). */
const mintViaPow = async (): Promise<string> => {
  let challengeRes: Response;
  try {
    challengeRes = await fetch(routes.api.QUOTE_JWT_CHALLENGE, {
      method: 'GET',
      cache: 'no-store',
    });
  } catch {
    throw new PowError('challenge_fetch');
  }

  // Remember the off verdict so later refreshes skip the probe this session.
  if (challengeRes.status === 404) {
    powState = 'off';
    return mintLegacy();
  }
  if (!challengeRes.ok) throw new PowError('challenge_http');

  powState = 'active';

  let challenge: unknown;
  try {
    challenge = await challengeRes.json();
  } catch {
    // A non-JSON 2xx body must hit the fallback, not block observe.
    throw new PowError('challenge_parse');
  }

  let solution: unknown;
  let path: 'worker' | 'main';
  let solveMs: number;
  try {
    const start = nowMs();
    ({ solution, path } = await solvePowChallenge(challenge));
    solveMs = Math.round(nowMs() - start);
  } catch (err) {
    // solvePowChallenge already tags worker-vs-main; preserve it. Anything else
    // (e.g. the dynamic import itself failing) is a main-path solve failure.
    throw err instanceof PowError ? err : new PowError('solve_main');
  }

  const mintRes = await fetch(routes.api.QUOTE_JWT, {
    method: 'POST',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      [MATCHA_POW_SOLVE_MS_HEADER]: String(solveMs),
      [MATCHA_POW_SOLVE_PATH_HEADER]: path,
    },
    body: JSON.stringify({ challenge, solution }),
  });
  if (!mintRes.ok) throw new PowError('mint_http');

  try {
    return acceptToken((await mintRes.json()) as MatchaJwt);
  } catch {
    // A bad mint body falls back too, rather than surfacing in observe.
    throw new PowError('mint_parse');
  }
};

/**
 * Fetches a short-lived quote JWT, cached until near expiry. The token cache means
 * PoW is paid at most once per ~60s; concurrent callers share one in-flight op.
 */
export const getQuoteJwt = async (): Promise<string> => {
  const now = Math.floor(Date.now() / 1000);
  if (cached && cached.exp - now > 10) return cached.token;
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      if (powState === 'off') return await mintLegacy();

      try {
        return await mintViaPow();
      } catch (err) {
        // Fall back tagged: observe must not block trading; enforce refuses the GET (403).
        if (err instanceof PowError) return await mintLegacy(err.reason);
        throw err;
      }
    } finally {
      inflight = null;
    }
  })();

  return inflight;
};

/** Drops the cached quote JWT so the next mint is fresh. */
export const clearQuoteJwt = (): void => {
  cached = null;
};

/** Returns a `Headers` with `MATCHA_JWT_HEADER` set to a fresh quote JWT. */
export const addQuoteJwtHeader = async (headers?: HeadersInit): Promise<Headers> => {
  const h = new Headers(headers || {});
  h.set(MATCHA_JWT_HEADER, await getQuoteJwt());
  return h;
};
